import { useState } from "react";

// elements/button is the styled Button used across the app - components/Button
// is an older one whose classes are not picked up by the global button styles.
import { Button } from "elements/button/Button";
import { Input } from "elements/input/Input";
import { SvgIcon } from "elements/svg-icon/svg-icon";
import { COLORS } from "models/colors";
import { VARIANTS } from "models/variants";
import { useAxios } from "providers/axios";
import { joinClassNames } from "utils/join-class-names";
import { bemElement } from "utils/bem-class-names";

import "./GiftCardRedeem.scss";

// Paying for a booking with a gift card.
//
// Built to sit directly under the saved cards on the checkout screen, and
// deliberately shaped like them: applied cards are rows in the same bordered
// container the credit cards use, and the button that opens the code field is
// the same full-width outlined button as "Add credit card" right above it.
//
// The customer types a code, we check it against the salon they are booking,
// and if it is good the code joins the list this component reports upwards.
// The parent re-prices the booking with those codes, and the amounts that come
// back are what we display - this component never works out how much a card
// covers, because that number has to come from the same place the charge does.
//
// The check here is only so the customer finds out immediately that a code is
// wrong. Everything is validated again server-side when the booking is made.

const baseClassName = "gift-card-redeem";
const bem = bemElement(baseClassName);

// Matches MAX_GIFT_CARDS_PER_ORDER in backend/src/services/giftCard.ts. Kept as
// a plain number rather than shared config because the server rejects a longer
// list anyway - this only stops the customer discovering that at the last step.
const MAX_CARDS = 5;

const onlyDigits = (value: string) => value.replace(/\D/g, "");

/** "837593459387" -> "8375 9345 9387", the way it appears in the gift email. */
const formatCode = (code: string) =>
  onlyDigits(code).replace(/(\d{4})(?=\d)/g, "$1 ");

type AppliedGiftCard = {
  code: string;
  formatted_code: string;
  amount: number;
};

type Props = {
  /** The location being booked. The server derives the company from it. */
  locationId?: string | null;
  /** Codes applied so far, in the order they were added. */
  codes: string[];
  onChange: (codes: string[]) => void;
  /** What each card actually covers, from the re-priced booking. */
  applied?: AppliedGiftCard[];
  /** True while the parent is re-pricing, so we do not invite a second code. */
  busy?: boolean;
  disabled?: boolean;
};

export function GiftCardRedeem({
  locationId,
  codes,
  onChange,
  applied = [],
  busy = false,
  disabled = false
}: Props) {
  const { api } = useAxios();
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  // The field starts behind a button. Most people booking are not paying with a
  // gift card, and an empty code box on a payment screen reads as something you
  // are supposed to fill in.
  const [open, setOpen] = useState(false);

  const atLimit = codes.length >= MAX_CARDS;

  // A code that was accepted but covers nothing still gets a row: it happens
  // when earlier cards already covered the whole booking, and silently dropping
  // it looks like the code was rejected.
  const rows = [
    ...applied,
    ...codes
      .filter((entry) => !applied.some((card) => card.code === entry))
      .map((entry) => ({
        code: entry,
        formatted_code: formatCode(entry),
        amount: 0
      }))
  ];

  const applyCode = async () => {
    const normalized = onlyDigits(code);

    if (!normalized) {
      setError("Enter the code from your gift card.");
      return;
    }

    if (codes.includes(normalized)) {
      setError("That gift card has already been added.");
      return;
    }

    if (atLimit) {
      setError(`You can use at most ${MAX_CARDS} gift cards on one booking.`);
      return;
    }

    setChecking(true);
    setError("");

    try {
      const result = await api
        .post("/v1/gift-cards/check", {
          code: normalized,
          location_id: locationId
        })
        .then((r) => r.data);

      // `found` and `is_spendable` are separate on purpose: a code that does
      // not exist, a card with nothing left on it and a disabled card are three
      // different things to be told at a checkout.
      if (!result?.found || !result?.is_spendable) {
        setError(result?.reason || "This gift card cannot be used.");
        return;
      }

      setCode("");
      setOpen(false);
      onChange([...codes, normalized]);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "We could not check that gift card. Please try again."
      );
    } finally {
      setChecking(false);
    }
  };

  const removeCode = (toRemove: string) => {
    setError("");
    onChange(codes.filter((existing) => existing !== toRemove));
  };

  if (!locationId) {
    return null;
  }

  return (
    <div className={baseClassName}>
      {rows.length > 0 && (
        <div className={bem("applied")}>
          {rows.map((card) => (
            <div className={bem("card")} key={card.code}>
              <div className={bem("card-left")}>
                <SvgIcon name="gift_card" className={bem("card-icon")} />
                <div className={bem("card-text")}>
                  <span className={bem("card-code")}>
                    {card.formatted_code || formatCode(card.code)}
                  </span>
                  <span className={bem("card-amount")}>
                    {card.amount > 0
                      ? `$${card.amount.toFixed(2)} applied`
                      : "Not needed for this booking"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className={bem("remove")}
                onClick={() => removeCode(card.code)}
                disabled={disabled || busy}
                aria-label={`Remove gift card ${card.formatted_code}`}
              >
                <SvgIcon name="close" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!atLimit && (
        <div className={bem("add")}>
          {open ? (
            <div className={bem("form")}>
              <Input
                label="Gift card code"
                value={code}
                placeholder="0000 0000 0000"
                disabled={disabled || checking}
                onChange={(next) => {
                  setCode(formatCode(String(next ?? "")));
                  if (error) setError("");
                }}
                errorMsg={error}
                showErrors={!!error}
                cyId="gift-card-code-input"
                withoutErrorAndHint={!error}
                focusOnMount
              />
              <div className={bem("form-actions")}>
                <Button
                  className={joinClassNames("flexible", bem("form-button"))}
                  color={COLORS.SECONDARY}
                  variant={VARIANTS.FILLED}
                  text={checking ? "Checking..." : "Apply"}
                  disabled={disabled || checking || busy}
                  submitting={checking}
                  onClick={applyCode}
                  cyId="gift-card-apply-button"
                />
                <Button
                  className={joinClassNames("flexible", bem("form-button"))}
                  color={COLORS.SECONDARY}
                  variant={VARIANTS.OUTLINED}
                  text="Cancel"
                  disabled={checking}
                  onClick={() => {
                    setOpen(false);
                    setCode("");
                    setError("");
                  }}
                />
              </div>
            </div>
          ) : (
            // The same button as "Add credit card" directly above it.
            <Button
              className={joinClassNames("flexible", bem("open-button"))}
              color={COLORS.SECONDARY}
              variant={VARIANTS.OUTLINED}
              iconLeftName="plus"
              text={rows.length > 0 ? "Add another gift card" : "Add gift card"}
              disabled={disabled}
              onClick={() => setOpen(true)}
              cyId="add-gift-card-button"
            />
          )}
        </div>
      )}

      {atLimit && (
        <p className={bem("hint")}>
          That is the most gift cards you can use on one booking.
        </p>
      )}
    </div>
  );
}

export default GiftCardRedeem;
